import defaultApi from '@plugins/axios'
import { user } from '@constants/URL'

/**
 * Content-Type explícito do caminho JSON.
 *
 * Não basta entregar um objeto ao axios: `plugins/axios.js` registra
 * `multipart/form-data` como header COMUM em produção, e ele venceria o
 * default do axios. O header vai junto do corpo, sempre.
 */
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** Métodos que a instância do axios expõe — o resto cai no `get` por default. */
const HTTP_TYPES_CONHECIDOS = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
  'options',
]

/**
 * O valor é um arquivo do browser?
 *
 * `Blob` cobre `File` (que herda dele) e os blobs que algumas telas montam a
 * partir de canvas/áudio. A checagem é defensiva porque este módulo também roda
 * no jsdom, onde `File` existe mas nem todo ambiente de teste o define.
 */
const ehArquivo = (valor) =>
  typeof Blob !== 'undefined' && valor instanceof Blob

/**
 * Serializa os dados em FormData — o caminho de SEMPRE.
 * @param {Object} data
 * @returns {FormData}
 */
const montarFormData = (data) => {
  const formData = new FormData()
  if (!data) return formData

  Object.entries(data).forEach(([key, value]) => {
    try {
      // ★ Lista de ARQUIVOS: uma parte por arquivo, todas sob a MESMA chave —
      // é assim que o `request.files.getlist(<chave>)` do backend as lê (o
      // upload de extratos da spec-004 aceita de 1 a 10). Sem este ramo, a
      // lista caía no `JSON.stringify` abaixo e virava a string "[{},{}]": a
      // requisição saía 200, sem arquivo nenhum dentro, e o erro só aparecia
      // como "nenhum arquivo enviado" do outro lado.
      if (Array.isArray(value) && value.length && value.every(ehArquivo)) {
        value.forEach((arquivo) => formData.append(key, arquivo))
        return
      }
      // Tratamento especial para arrays e objetos
      if (value && typeof value === 'object' && value.length !== undefined) {
        formData.append(key, JSON.stringify(value))
      } else {
        formData.append(key, value)
      }
    } catch (e) {
      console.error(e)
      formData.append(key, value)
    }
  })
  return formData
}

/**
 * Argumentos posicionais do axios depois da URL.
 *
 * DELETE carrega o corpo dentro do config; os demais o passam direto. O header
 * só existe no caminho JSON — no de FormData, deixar o browser montar o
 * boundary é o que faz o multipart funcionar.
 *
 * @param {string} type
 * @param {FormData|Object} corpo
 * @param {boolean} enviarComoJson
 * @returns {Array} argumentos após a URL
 */
const montarArgumentos = (type, corpo, enviarComoJson, timeout) => {
  const config = {}
  if (enviarComoJson) config.headers = JSON_HEADERS
  if (timeout) config.timeout = timeout

  if (type === 'delete') return [{ ...config, data: corpo }]

  // GET/OPTIONS não levam corpo: no axios o 2º argumento JÁ é o config, e é ali
  // que o timeout precisa entrar. Sem timeout a forma da chamada não muda — o
  // corpo segue nessa posição como sempre seguiu.
  //
  // ⚠️ Com timeout, o `corpo` do GET é DESCARTADO — não há posição para os dois.
  // Isso não perde nada hoje: o corpo de um GET já era um FormData que o axios
  // ignorava (nenhum GET do projeto trafega dados por ele; quem tem parâmetro
  // usa querystring via `formatQueryParams`). Se algum dia um GET precisar de
  // `params` E de prazo, os dois têm que ir no MESMO objeto de config.
  if (timeout && (!type || type === 'get')) return [config]

  return Object.keys(config).length ? [corpo, config] : [corpo]
}

/**
 * O deadline estourou no CLIENTE (axios aborta com `ECONNABORTED`), não no
 * servidor. Merece status próprio: um 500 genérico mandaria a tela dizer "erro
 * inesperado" quando o que houve foi demora — e demora tem outra saída
 * (tentar de novo) e outra explicação para quem está esperando.
 */
const ehTimeoutDoCliente = (error) =>
  !!error &&
  !error.response &&
  (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))

/** Log tabular do multipart, preservado do comportamento original. */
const logarFormData = (formData) => {
  const params = []
  for (const [key, value] of formData.entries()) {
    params.push({ key, value })
  }
  if (params.length) {
    console.table(params)
  }
}

/** Pré-condições da chamada: URL obrigatória, tipo HTTP conhecido. */
const validarChamada = (url, type) => {
  if (!url) {
    console.error('URL não informada')
    throw new Error('URL não informada')
  }
  if (!HTTP_TYPES_CONHECIDOS.includes(type || 'get')) {
    console.info('Tipo de requisição invalida, utilizando get')
  }
}

/** Manda o usuário ao login preservando para onde ele queria ir. */
const redirecionarParaLogin = () => {
  console.warn('Sessão expirada')
  globalThis.location.href =
    user.tela_login +
    '?redirect=' +
    (globalThis.location.pathname + globalThis.location.search)
}

/** Validações e logs do request — ruído que não precisa inflar `get`. */
const logarRequest = (url, data, type, corpo, enviarComoJson) => {
  if (url.startsWith('?')) {
    console.error(`Rota no formato incorretor: [${type || 'GET'}] ${url}`, data)
  }
  console.log('\nEnviando: ', corpo, 'URL: ', url, '\nType: ', type)
  if (!enviarComoJson) logarFormData(corpo)
}

/** Log da resposta, com o caso da página HTML preservado. */
const logarResponse = (response, url, type) => {
  console.log('tipo', typeof response.data)
  const ehHtml =
    typeof response.data === 'string' &&
    response.data.includes('<!DOCTYPE html>')
  if (ehHtml) {
    console.log('Recebendo: ', 'Página html', 'URL: ', url, '\nType: ', type)
  } else {
    console.log('Recebendo: ', response.data, 'URL: ', url, '\nType: ', type)
  }
  console.log('\n')
}

/**
 * Converte a rejeição do axios no envelope AAWZ, sempre resolvendo.
 *
 * Vive fora de `get` de propósito: inline, os dois eram um único bloco de ~40
 * statements que o ESLint já acusava antes deste arquivo ganhar o caminho de
 * timeout.
 *
 * @param {Error} error
 * @param {boolean} ignoreErro
 * @returns {{data: {status: number, message: string, value: *}}}
 */
const tratarErroDeRequest = (error, ignoreErro) => {
  if (!ignoreErro) {
    console.log(error)
    console.log('error', typeof error, JSON.stringify(error))
  }
  console.error(error)

  if (error.response && error.response.status === 401) {
    redirecionarParaLogin()
  }
  console.warn(error.response)

  if (ehTimeoutDoCliente(error)) {
    return {
      data: {
        status: 408,
        message: 'A requisição demorou mais que o esperado.',
        value: error,
      },
    }
  }

  const status = error.response ? error.response.status : 500
  return {
    data: {
      status: status,
      message:
        (error.message || 'Erro inesperado, tente novamente mais tarde') +
        `(${status})`,
      value: error,
    },
  }
}

/**
 * Cria uma instância de conexão com a API
 * @param {Object} api - Instância do axios para realizar as requisições (opcional)
 * @returns {Object} Objeto com métodos HTTP (get, post, put, delet) e ping
 */
export const createConnection = (api = defaultApi) => {
  /** Dispara a chamada no axios com os argumentos posicionais certos. */
  const dispararRequest = ({ url, type, corpo, enviarComoJson, timeout }) => {
    if (type === 'options') return api.options(url)
    // Fallback REAL para GET quando o tipo não existe no axios. `validarChamada`
    // já avisava "utilizando get", mas a linha seguinte chamava
    // `api[tipoInvalido]` e estourava TypeError — a mensagem prometia um
    // fallback que nunca existiu.
    const metodo = typeof api[type] === 'function' ? type : 'get'
    return api[metodo](
      url,
      ...montarArgumentos(metodo, corpo, enviarComoJson, timeout)
    )
  }

  /**
   * Função base para realizar requisições HTTP
   * @param {string} url - URL da requisição
   * @param {Object} data - Dados a serem enviados
   * @param {string} type - Tipo de requisição HTTP (get, post, put, delete)
   * @param {boolean} ignoreErro - Flag para ignorar tratamento de erros
   * @param {{json?: boolean, timeout?: number}} options - `json: true` envia o corpo como JSON
   *   em vez de FormData. OPT-IN: omitir mantém o FormData, que é o que as
   *   rotas com `location='form'`/`request.form` do backend esperam — e é a
   *   esmagadora maioria delas. Use apenas quando o `reqparse` do recurso
   *   declarar `location='json'`, senão o backend responde
   *   "Missing required parameter in the JSON body".
   *   `timeout: <ms>` dá prazo ao request; ao estourar, o envelope volta com
   *   status 408 (ver `ehTimeoutDoCliente`).
   * @returns {Promise<Object>} Resposta da requisição
   */
  async function get(url, data, type, ignoreErro = false, options = {}) {
    const enviarComoJson = !!(options && options.json)
    // `timeout` (ms) é OPT-IN por request. Não existe timeout global (ver
    // plugins/axios.js): a plataforma tem chamadas legitimamente longas — chat
    // de IA, por exemplo — que um deadline global cortaria no meio. Quem sabe
    // que a própria rota deve ter prazo, declara o prazo.
    const timeout = (options && options.timeout) || 0

    validarChamada(url, type)

    const corpo = enviarComoJson ? data || {} : montarFormData(data)
    logarRequest(url, data, type, corpo, enviarComoJson)

    const request = dispararRequest({
      url,
      type,
      corpo,
      enviarComoJson,
      timeout,
    })
    const response = await request.catch((error) =>
      tratarErroDeRequest(error, ignoreErro)
    )

    // Tratamento de resposta 401
    if (response.status === 401) {
      redirecionarParaLogin()
      return {}
    }

    logarResponse(response, url, type)

    return response.data || {}
  }

  /**
   * Realiza uma requisição POST
   * @param {string} url - URL da requisição
   * @param {Object} data - Dados a serem enviados
   * @param {boolean} ignoreErro - Flag para ignorar tratamento de erros
   */
  function post(url, data, ignoreErro = false, options = {}) {
    return get(url, data, 'post', ignoreErro, options)
  }

  /**
   * Realiza uma requisição PUT
   * @param {string} url - URL da requisição
   * @param {Object} data - Dados a serem enviados
   * @param {boolean} ignoreErro - Flag para ignorar tratamento de erros
   */
  function put(url, data, ignoreErro = false, options = {}) {
    return get(url, data, 'put', ignoreErro, options)
  }

  /**
   * Realiza uma requisição DELETE
   * @param {string} url - URL da requisição
   * @param {Object} data - Dados a serem enviados
   * @param {boolean} ignoreErro - Flag para ignorar tratamento de erros
   */
  function delet(url, data, ignoreErro = false, options = {}) {
    return get(url, data, 'delete', ignoreErro, options)
  }

  /**
   * Realiza uma requisição PATCH
   * @param {string} url - URL da requisição
   * @param {Object} data - Dados a serem enviados
   * @param {boolean} ignoreErro - Flag para ignorar tratamento de erros
   */
  function patch(url, data, ignoreErro = false, options = {}) {
    return get(url, data, 'patch', ignoreErro, options)
  }

  /**
   * @param {string} url - URL da requisição
   */
  function options(url) {
    return get(url, null, 'options')
  }

  // Interface pública do módulo
  return {
    get,
    post,
    put,
    delet,
    options,
    patch,
    /**
     * Função de teste de conectividade
     * @returns {string} Retorna 'pong'
     */
    ping: () => {
      console.log('pong')
      return 'pong'
    },
  }
}

// Exporta uma instância padrão do connection utilizando a api padrão
export default createConnection(defaultApi)
